// src/components/proposals/ConfigurableAgentBlock.tsx

import React from 'react';
import { Card, Avatar, ToggleSwitchField } from '@dopomogai/ui';
import { ArrowRight } from 'lucide-react';

// --- TYPE DEFINITIONS ---
export type Feature = {
  key: string;
  title: string;
  description: string;
  price: number;
  eta_business_days: number;
  category: string;
};
export type FeatureBundle = { tier: string; title: string; feature_keys: string[] };
export type Agent = {
  id: string;
  name: string;
  avatar_url: string;
  value_proposition: string;
  key_outcomes: string[];
  feature_bundles: FeatureBundle[];
};

// --- PROPS INTERFACE ---
type Props = {
  agent: Agent;
  catalogMap: Record<string, Feature>;
  selectedFeatures: Record<string, boolean>;
  onFeatureToggle: (key: string, enabled: boolean) => void;
};

const formatCurrency = (amount: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);

/**
 * **FIXED:** A new internal component to handle the complex layout required.
 * It renders our custom label and the ToggleSwitchField side-by-side.
 */
const FeatureToggleRow: React.FC<{
    feature: Feature;
    isSelected: boolean;
    onToggle: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ feature, isSelected, onToggle }) => {
    return (
        <div className="flex items-start justify-between gap-4">
            {/* The complex label is now outside the ToggleSwitchField */}
            <div className="flex-grow">
                <span className="font-semibold text-slate-900">{feature.title}</span>
                <p className="text-sm text-slate-600">{feature.description}</p>
            </div>
            <div className="text-right ml-4 shrink-0">
                <div className="font-bold text-slate-800">{formatCurrency(feature.price)}</div>
                <div className="text-xs text-slate-500">{feature.eta_business_days} days</div>
            </div>
            {/* The actual switch, with its own label hidden */}
            <ToggleSwitchField
                checked={isSelected}
                onChange={onToggle}
                label={feature.title} // Still pass for accessibility
                labelPosition="hidden" // Hide the visual label
            />
        </div>
    );
};

export const ConfigurableAgentBlock: React.FC<Props> = ({ agent, catalogMap, selectedFeatures, onFeatureToggle }) => {
  return (
    <Card noPadding id={agent.id}>
      <div className="p-5 md:p-6">
        {/* Agent Header */}
        <div className="flex items-start gap-4 mb-4">
          <Avatar src={agent.avatar_url} alt={agent.name} size="lg" />
          <div>
            <h3 className="text-2xl font-extrabold text-slate-900">{agent.name}</h3>
            <p className="text-slate-700 mt-1">{agent.value_proposition}</p>
          </div>
        </div>

        {/* Key Outcomes */}
        <div className="mb-6">
          <h4 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-2">Key Outcomes</h4>
          <ul className="space-y-2">
            {agent.key_outcomes.map((outcome) => (
              <li key={outcome} className="flex items-start gap-2 text-slate-800">
                <ArrowRight size={16} className="text-emerald-500 mt-1 shrink-0" />
                <span>{outcome}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Feature Bundles & Toggles */}
        <div className="space-y-6">
          {agent.feature_bundles.map((bundle) => (
            <div key={bundle.tier}>
              <h4 className="text-lg font-bold text-slate-800 border-b-2 border-slate-200 pb-2 mb-3">{bundle.title}</h4>
              <div className="space-y-4">
                {bundle.feature_keys.map((key) => {
                  const feature = catalogMap[key];
                  if (!feature) return null;
                  
                  return (
                    <FeatureToggleRow
                        key={key}
                        feature={feature}
                        isSelected={!!selectedFeatures[key]}
                        onToggle={(e) => onFeatureToggle(key, e.target.checked)}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default ConfigurableAgentBlock;